import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { EditableSelect } from '@/components/inventory/EditableItemField';
import {
  fetchArchiveReadiness,
  useArchiveItem,
  useInventoryLevels,
  useScrapItemInventory,
  useUpdateItem,
  type ArchiveInventoryBalance,
  type ArchiveReadiness,
  type Item,
  type ItemStatus,
} from '@/features/inventory/hooks';
import { ITEM_STATUS_OPTIONS, getItemStatusMeta } from '@/features/inventory/itemStatus';
import { useAuth } from '@/app/AuthProvider';
import { formatError } from '@/lib/errors';
import { canWrite } from '@/lib/permissions';

interface ItemStatusControlProps {
  item: Item;
  canEdit: boolean;
  density?: 'cell' | 'kv';
  align?: 'left' | 'right';
}

/**
 * Status editor for the catalog grid and the item page. Choosing
 * Archived with nothing on hand just saves. Choosing it while a
 * location still has quantity opens a disposition dialog so the stock
 * is scrapped (or a negative bucket is brought back to zero) before
 * the status changes.
 */
export function ItemStatusControl({
  item,
  canEdit,
  density = 'cell',
  align = 'left',
}: ItemStatusControlProps) {
  const updateItem = useUpdateItem();
  const archiveItem = useArchiveItem();
  const [readiness, setReadiness] = useState<ArchiveReadiness | null>(null);
  const meta = getItemStatusMeta(item.status);

  const saveStatus = async (next: ItemStatus) => {
    try {
      await updateItem.mutateAsync({ id: item.id, patch: { status: next } });
    } catch (err) {
      throw new Error(formatError(err, 'Could not update the status.'));
    }
  };

  return (
    <>
      <EditableSelect<ItemStatus>
        value={item.status}
        options={ITEM_STATUS_OPTIONS}
        ariaLabel="Status"
        canEdit={canEdit}
        density={density}
        align={align}
        display={<Badge variant={meta.variant}>{meta.label}</Badge>}
        onSave={async (next) => {
          if (next !== 'archived' || item.status === 'archived') {
            await saveStatus(next);
            return;
          }
          let nextReadiness: ArchiveReadiness;
          try {
            nextReadiness = await fetchArchiveReadiness(item.id);
          } catch (err) {
            throw new Error(formatError(err, 'Could not check on-hand inventory.'));
          }
          if (
            nextReadiness.balances.length === 0 &&
            nextReadiness.openPurchaseOrders.length === 0
          ) {
            await saveStatus('archived');
            return;
          }
          setReadiness(nextReadiness);
        }}
      />
      {readiness && (
        <ArchiveDispositionDialog
          item={item}
          readiness={readiness}
          purpose="archive"
          pending={archiveItem.isPending}
          onClose={() => {
            if (!archiveItem.isPending) setReadiness(null);
          }}
          onConfirm={async () => {
            try {
              const result = await archiveItem.mutateAsync({ itemId: item.id, sku: item.sku });
              setReadiness(null);
              toast.success(archiveSuccessMessage(item, result));
            } catch (err) {
              toast.error(formatError(err, 'Could not archive the item.'));
            }
          }}
        />
      )}
    </>
  );
}

/**
 * Shown on an item that is already archived but still has a quantity.
 * Items renamed from Discontinued can be in this state; the banner is
 * how that stock gets scrapped so the totals stay honest.
 */
export function ArchivedStockBanner({ item }: { item: Item }) {
  const { role } = useAuth();
  const canEdit = canWrite(role, 'inventory');
  const { data: levels } = useInventoryLevels(item.id);
  const scrap = useScrapItemInventory();
  const [readiness, setReadiness] = useState<ArchiveReadiness | null>(null);
  const [opening, setOpening] = useState(false);

  if (item.status !== 'archived') return null;
  const outstanding = (levels ?? []).filter((level) => Number(level.quantity) !== 0);
  if (outstanding.length === 0) return null;

  const total = outstanding.reduce((sum, level) => sum + Number(level.quantity), 0);

  return (
    <>
      <div className="flex flex-col gap-3 rounded-md border border-warning/40 bg-warning/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground">
          <span className="font-medium">{item.sku}</span> is archived and still has{' '}
          <span className="font-medium tabular-nums">
            {total.toLocaleString()} {item.base_uom}
          </span>{' '}
          on hand. Scrap or clear that quantity so inventory totals stay accurate.
        </p>
        {canEdit && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0"
            disabled={opening || scrap.isPending}
            onClick={async () => {
              setOpening(true);
              try {
                const next = await fetchArchiveReadiness(item.id);
                if (next.balances.length === 0) {
                  toast.success('On-hand is already zero.');
                  return;
                }
                setReadiness(next);
              } catch (err) {
                toast.error(formatError(err, 'Could not load on-hand.'));
              } finally {
                setOpening(false);
              }
            }}
          >
            Review stock
          </Button>
        )}
      </div>
      {readiness && (
        <ArchiveDispositionDialog
          item={item}
          readiness={readiness}
          purpose="clear"
          pending={scrap.isPending}
          onClose={() => {
            if (!scrap.isPending) setReadiness(null);
          }}
          onConfirm={async () => {
            try {
              const result = await scrap.mutateAsync({ itemId: item.id, sku: item.sku });
              setReadiness(null);
              toast.success(clearSuccessMessage(item, result));
            } catch (err) {
              toast.error(formatError(err, 'Could not clear the inventory.'));
            }
          }}
        />
      )}
    </>
  );
}

function archiveSuccessMessage(
  item: Item,
  result: { scrapped: number; corrected: number },
): string {
  if (result.scrapped > 0) {
    return `Scrapped ${result.scrapped.toLocaleString()} ${item.base_uom} and archived ${item.sku}.`;
  }
  if (result.corrected > 0) {
    return `Cleared the on-hand balance and archived ${item.sku}.`;
  }
  return `Archived ${item.sku}.`;
}

function clearSuccessMessage(item: Item, result: { scrapped: number; corrected: number }): string {
  if (result.scrapped > 0) {
    return `Scrapped ${result.scrapped.toLocaleString()} ${item.base_uom} of ${item.sku}.`;
  }
  return `Cleared the on-hand balance for ${item.sku}.`;
}

function ArchiveDispositionDialog({
  item,
  readiness,
  purpose,
  pending,
  onClose,
  onConfirm,
}: {
  item: Item;
  readiness: ArchiveReadiness;
  purpose: 'archive' | 'clear';
  pending: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const balances = readiness.balances;
  const hasStock = balances.length > 0;
  const hasScrap = balances.some((balance) => balance.quantity > 0);
  const hasNegative = balances.some((balance) => balance.quantity < 0);
  const openOrders = readiness.openPurchaseOrders;

  const title = hasStock
    ? purpose === 'archive'
      ? `Archive ${item.name}?`
      : `Clear stock for ${item.name}?`
    : `Archive ${item.name}?`;

  const confirmLabel = !hasStock
    ? 'Archive item'
    : hasScrap
      ? purpose === 'archive'
        ? 'Scrap stock and archive'
        : 'Scrap remaining stock'
      : purpose === 'archive'
        ? 'Clear inventory and archive'
        : 'Clear inventory';

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {hasStock
              ? purpose === 'archive'
                ? `${item.sku} still has inventory. Archiving takes it off purchase orders, stock receipts, and production, so the on-hand quantity has to leave inventory first. Scrapping writes an issue to the stock ledger — the quantity is not deleted.`
                : `${item.sku} is already archived and still has inventory. Scrap the remaining quantity so on-hand stays accurate. The issue is written to the stock ledger.`
              : `${item.sku} is still on an open purchase order. Archiving removes it from purchasing and receiving, so those lines cannot be received afterward.`}
          </DialogDescription>
        </DialogHeader>

        {hasStock && (
          <div className="space-y-3">
            <BalanceList balances={balances} uom={item.base_uom} />
            <p className="text-xs text-muted-foreground">
              {hasNegative && hasScrap
                ? 'Positive quantities are issued as scrap. Negative quantities are adjusted up to zero so a location is not left below nothing.'
                : hasNegative
                  ? 'These balances are below zero. They will be adjusted up to zero.'
                  : 'Each balance is issued as scrap, which takes it out of on-hand and keeps the movement on the ledger.'}{' '}
              {purpose === 'archive'
                ? 'To transfer the stock or write it off a different way, cancel and record that movement first.'
                : 'Cancel if this quantity should be written off a different way.'}
            </p>
          </div>
        )}

        {openOrders.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Open purchase orders</p>
            <ul className="divide-y divide-border rounded-md border border-border">
              {openOrders.slice(0, 6).map((order) => (
                <li
                  key={order.poNumber}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="font-mono text-xs">{order.poNumber}</span>
                  <span className="text-muted-foreground">
                    {order.outstanding.toLocaleString()} still to receive
                  </span>
                </li>
              ))}
            </ul>
            {openOrders.length > 6 && (
              <p className="text-xs text-muted-foreground">
                + {openOrders.length - 6} more open orders
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void onConfirm()} disabled={pending}>
            {pending ? 'Saving…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BalanceList({ balances, uom }: { balances: ArchiveInventoryBalance[]; uom: string }) {
  return (
    <ul className="divide-y divide-border rounded-md border border-border">
      {balances.map((balance) => (
        <li key={balance.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
          <span className="min-w-0">
            <span className="block truncate">{balance.locationName}</span>
            {balance.lotNumber && (
              <span className="block font-mono text-[11px] text-muted-foreground">
                Lot {balance.lotNumber}
              </span>
            )}
          </span>
          <span className="shrink-0 text-right">
            <span className="block tabular-nums">
              {balance.quantity.toLocaleString()} {uom}
            </span>
            <span className="block text-[11px] text-muted-foreground">
              {balance.quantity > 0 ? 'Scrap' : 'Adjust to zero'}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
